<script lang="ts">
	import { format, locale } from 'svelte-i18n';

	import type { CameraCloudStorageBenefit } from 'src/types/Subscriptions';

	import BenefitBadges from '../Premium/BenefitBadges.svelte';
	import { Icon, Loader, Text } from '@Shelly/shelly-design-system';

	import {
		cameraCloudStorageBenefits,
		mapCmsItemsToCameraCloudStorageBenefits,
	} from 'src/helpers/subscription_benefits_helpers';
	import { fetchBenefits } from 'src/services/changelog';

	const CAMERA_STORAGE_ACCENT = '#b26eff';

	let benefitsList: CameraCloudStorageBenefit[] = [];
	let isLoading = true;
	let requestSeq = 0;

	$: standardColumnLabel = $format('label_standard', { default: 'Standard' });
	$: featuredColumnLabel = $format('text_camera_cloud_storage_label', {
		default: 'Camera Storage',
	});

	const useFallback = () => {
		benefitsList = cameraCloudStorageBenefits;
	};

	/** CMS translations carry line breaks as the escaped two-character sequence. */
	const withLineBreaks = (value: string) => value.replace(/\\n/g, '\n');

	const loadBenefits = async (currentLocale: string) => {
		const reqId = ++requestSeq;
		isLoading = true;

		try {
			const data = await fetchBenefits(currentLocale, 'camera_cloud_storage');
			if (reqId !== requestSeq) return;

			if (data?.items.length) {
				benefitsList = mapCmsItemsToCameraCloudStorageBenefits(data.items);
			} else {
				useFallback();
			}
		} catch {
			if (reqId !== requestSeq) return;
			useFallback();
		} finally {
			if (reqId === requestSeq) isLoading = false;
		}
	};

	/**
	 * Track the displayed UI locale rather than a persisted settings field.
	 * CMS translations must match the language the user is actually seeing.
	 */
	$: if ($locale) {
		loadBenefits($locale);
	}
</script>

{#if isLoading && benefitsList.length === 0}
	<div class="d-flex justify-content-center p-m">
		<Loader size="m" />
	</div>
{:else}
	<div class="ccs-benefits">
		<div class="ccs-benefit-header">
			<div class="ccs-column-header ccs-column-header--description">
				<Text styleType="body-small" class="text-uppercase text-color-muted text-bold" noWrap>
					{$format('text_camera_cloud_storage_what_you_get', { default: 'What you get' })}
				</Text>
			</div>
			<div class="ccs-column-header ccs-standard-label">
				<Text styleType="body-small" class="ccs-header-label text-uppercase text-bold" noWrap>
					{standardColumnLabel}
				</Text>
				<span class="ccs-header-icon" role="img" aria-label={standardColumnLabel}>
					<span class="ccs-header-icon-badge">
						<Icon icon="fak fa-solid-shelly" size="s" />
					</span>
				</span>
			</div>
			<div class="ccs-column-header ccs-storage-label">
				<Text styleType="body-small" class="ccs-header-label text-uppercase text-bold" noWrap>
					{featuredColumnLabel}
				</Text>
				<span class="ccs-header-icon" role="img" aria-label={featuredColumnLabel}>
					<span class="ccs-header-icon-badge">
						<Icon icon="fas fa-camera" size="s" />
					</span>
				</span>
			</div>
		</div>

		{#each benefitsList as benefit (benefit.title)}
			<div class="ccs-benefit-row">
				<div class="ccs-benefit-description">
					<div class="d-flex align-items-center flex-wrap mb-xs gap-xs">
						<Text styleType="controls-small" class="text-bold">
							{$format(benefit.title, { default: benefit.titleDefault })}
						</Text>
						<BenefitBadges
							isAI={benefit.isAI}
							isNew={benefit.new}
							betaFlag={benefit.betaFlag}
							comingSoon={benefit.comingSoon}
						/>
					</div>
					<Text styleType="body-small" breakWord class="text-color-muted ccs-benefit-subtitle">
						{withLineBreaks($format(benefit.subtitle, { default: benefit.subtitleDefault }))}
					</Text>
				</div>

				{#each [benefit.standard, benefit.cameraStorage] as value, column}
					<div class:ccs-storage-value={column === 1} class="ccs-benefit-value">
						{#if typeof value === 'boolean'}
							<Icon
								size="s"
								icon={value ? 'fas fa-check' : 'fas fa-times'}
								style={column === 1 && value
									? `color: ${CAMERA_STORAGE_ACCENT};`
									: 'color: #8c9baa;'}
							/>
						{:else}
							{#if value.icon}
								<Icon
									size="s"
									icon="fas fa-check"
									style={column === 1
										? `color: ${CAMERA_STORAGE_ACCENT};`
										: 'color: #8c9baa;'}
								/>
							{/if}
							{#if value.value}
								<span class="ccs-benefit-value-primary">{value.value}</span>
							{/if}
							<span class="ccs-benefit-value-label">
								{$format(value.label, { default: value.labelDefault })}
							</span>
						{/if}
					</div>
				{/each}
			</div>
		{/each}
	</div>
{/if}

<style>
	.ccs-benefits {
		width: 100%;
	}

	.ccs-benefit-header,
	.ccs-benefit-row {
		display: grid;
		grid-template-columns: minmax(0, 1fr) repeat(2, minmax(104px, 184px));
	}

	.ccs-benefit-header {
		align-items: center;
		min-height: 40px;
	}

	.ccs-benefit-header :global(.text) {
		min-width: 0;
	}

	:global(.ccs-standard-label),
	:global(.ccs-storage-label) {
		text-align: center;
	}

	.ccs-column-header,
	.ccs-header-icon {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.ccs-column-header--description {
		justify-content: flex-start;
	}

	.ccs-header-icon {
		display: none;
		width: 100%;
	}

	.ccs-header-icon-badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;
		width: 36px;
		height: 36px;
		border: 1px solid transparent;
		border-radius: 50%;
	}

	:global(.ccs-standard-label) {
		color: var(--color-primary, #2491ff);
	}

	.ccs-standard-label :global(.ccs-header-label) {
		color: #ffffff;
	}

	.ccs-standard-label .ccs-header-icon-badge {
		border-color: rgba(36, 145, 255, 0.42);
		background: linear-gradient(145deg, rgba(36, 145, 255, 0.24), rgba(36, 145, 255, 0.08));
		box-shadow: 0 8px 22px -12px rgba(36, 145, 255, 0.95);
	}

	:global(.ccs-storage-label),
	.ccs-storage-value {
		color: #b26eff;
	}

	.ccs-storage-label .ccs-header-icon-badge {
		border-color: rgba(178, 110, 255, 0.5);
		background: linear-gradient(145deg, rgba(219, 92, 244, 0.25), rgba(142, 54, 236, 0.1));
		box-shadow: 0 8px 22px -12px rgba(178, 110, 255, 1);
	}

	.ccs-benefit-row {
		align-items: stretch;
		border-bottom: 1px solid var(--color-2, #303235);
	}

	.ccs-benefit-row:last-child {
		border-bottom: none;
	}

	.ccs-benefit-description {
		display: flex;
		flex-direction: column;
		justify-content: center;
		min-width: 0;
		padding: 8px 0;
	}

	.ccs-benefit-description :global(.ccs-benefit-subtitle) {
		white-space: pre-line;
	}

	.ccs-benefit-value {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 2px;
		min-width: 0;
		padding: 8px;
		text-align: center;
	}

	.ccs-benefit-value-primary {
		font-size: 13px;
		font-weight: 700;
		line-height: 16px;
	}

	.ccs-benefit-value-label {
		font-size: 10px;
		font-weight: 500;
		line-height: 12px;
	}

	@media screen and (max-width: 639px) {
		:global(.ccs-header-label) {
			display: none !important;
		}

		.ccs-header-icon {
			display: flex;
		}

		.ccs-benefit-header,
		.ccs-benefit-row {
			grid-template-columns: minmax(130px, 1fr) repeat(2, 104px);
		}

		.ccs-benefits {
			overflow-x: auto;
		}
	}
</style>
